import { Stack } from '@phosphor-icons/react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { readMinutes, timeAgo } from '@/lib/format';
import { useCategoryLookup, useSourceMap } from '@/lib/hooks';
import { useT } from '@/lib/i18n';
import type { Story } from '@/lib/types';
import { Img, Skeleton, SourceLogo } from './primitives';

export function storyHref(s: { id: string }) {
  return `/s/${s.id}`;
}

export function StoryMeta({ story, showCat = true, light }: { story: Story; showCat?: boolean; light?: boolean }) {
  const cats = useCategoryLookup();
  const { data: sources } = useSourceMap();
  const t = useT();
  const src = sources?.get(story.source_id);
  const color = cats.color(story.category_id);
  const name = cats.name(story.category_id);
  return (
    <div className="meta">
      {showCat && name && (
        <span className="cat-label" style={{ '--c': light ? '#fff' : color } as CSSProperties}>
          <span className="dot" style={{ '--c': color } as CSSProperties} />
          {name}
        </span>
      )}
      {showCat && name && <span className="meta-sep" />}
      {story.is_synthesis ? (
        <span className="nowrap" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Stack size={13} weight="bold" />
          {t('story_multi')}
        </span>
      ) : (
        src && (
          <span className="src" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <SourceLogo src={src.logo_url} site={src.site_url} />
            {src.title}
          </span>
        )
      )}
      <span className="meta-sep" />
      <span>{timeAgo(story.published_at ?? story.first_seen_at)}</span>
    </div>
  );
}

export function HeroStory({ story }: { story: Story }) {
  const cats = useCategoryLookup();
  const t = useT();
  return (
    <Link to={storyHref(story)} className="hero-main" style={{ '--c': cats.color(story.category_id) } as CSSProperties}>
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} eager width={820} label={cats.name(story.category_id)} sizes="(max-width: 999px) 100vw, 820px" />
      <div className="hero-body">
        <StoryMeta story={story} light />
        <h2 className="hero-title">{story.heading}</h2>
        {story.dek && <p className="hero-dek clamp-2">{story.dek}</p>}
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <span className="badge badge-dark">{t('story_read_min', { n: readMinutes(story.summary_word_count) })}</span>
        </div>
      </div>
    </Link>
  );
}

export function SideStory({ story }: { story: Story }) {
  const cats = useCategoryLookup();
  return (
    <Link to={storyHref(story)} className="side-story">
      <div style={{ minWidth: 0 }}>
        <StoryMeta story={story} />
        <h3 className="clamp-3">{story.heading}</h3>
      </div>
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={110} />
    </Link>
  );
}

export function StoryTile({ story, selected, read, showDek = true }: { story: Story; selected?: boolean; read?: boolean; showDek?: boolean }) {
  const cats = useCategoryLookup();
  return (
    <Link
      to={storyHref(story)}
      className={`tile${selected ? ' selected' : ''}${read ? ' read' : ''}`}
      data-story-id={story.id}
      aria-current={selected ? 'true' : undefined}
    >
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={440} label={cats.name(story.category_id)} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <StoryMeta story={story} />
        <h3 className="clamp-3">{story.heading}</h3>
        {showDek && story.dek && <p className="clamp-2">{story.dek}</p>}
      </div>
    </Link>
  );
}

export function RowStory({ story, selected }: { story: Story; selected?: boolean }) {
  const cats = useCategoryLookup();
  return (
    <Link to={storyHref(story)} className={`row-story${selected ? ' tile selected' : ''}`} data-story-id={story.id}>
      <div style={{ minWidth: 0 }}>
        <StoryMeta story={story} />
        <h3 className="clamp-3">{story.heading}</h3>
        {story.dek && <p className="clamp-2">{story.dek}</p>}
      </div>
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={150} />
    </Link>
  );
}

export function RankItem({ story }: { story: Story }) {
  const cats = useCategoryLookup();
  return (
    <Link to={storyHref(story)} className="rank-item">
      <div style={{ minWidth: 0 }}>
        <StoryMeta story={story} />
        <h3 className="clamp-2" style={{ marginTop: 5 }}>
          {story.heading}
        </h3>
      </div>
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={72} />
    </Link>
  );
}

export function MiniStory({ story }: { story: Story }) {
  const cats = useCategoryLookup();
  return (
    <Link to={storyHref(story)} className="mini-story">
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={64} />
      <div style={{ minWidth: 0 }}>
        <h4 className="clamp-2">{story.heading}</h4>
        <div className="meta" style={{ marginTop: 4 }}>
          <span>{timeAgo(story.published_at ?? story.first_seen_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export function TileSkeleton() {
  return (
    <div className="tile">
      <Skeleton h={undefined} style={{ aspectRatio: '16/10', height: 'auto' }} r={16} />
      <Skeleton h={12} w="40%" />
      <Skeleton h={18} />
      <Skeleton h={18} w="75%" />
    </div>
  );
}

export function GridSkeleton({ n = 6 }: { n?: number }) {
  return (
    <div className="grid">
      {Array.from({ length: n }, (_, i) => (
        <TileSkeleton key={i} />
      ))}
    </div>
  );
}

/** Başlığı görselin üzerinde büyük kart (öne çıkanlar). */
export function FeatureTile({ story, selected }: { story: Story; selected?: boolean }) {
  const cats = useCategoryLookup();
  const t = useT();
  return (
    <Link
      to={storyHref(story)}
      className={`feature-tile${selected ? ' selected' : ''}`}
      data-story-id={story.id}
      style={{ '--c': cats.color(story.category_id) } as CSSProperties}
    >
      <Img src={story.cover_image_url} color={cats.color(story.category_id)} width={620} label={cats.name(story.category_id)} />
      <div className="feature-body">
        <StoryMeta story={story} light />
        <h3 className="clamp-3">{story.heading}</h3>
        {story.dek && <p className="clamp-2">{story.dek}</p>}
        <span className="badge badge-dark" style={{ marginTop: 10, alignSelf: 'flex-start' }}>
          {t('story_read_min', { n: readMinutes(story.summary_word_count) })}
        </span>
      </div>
    </Link>
  );
}

/**
 * Hikâye ızgarası. `featured` kadar ilk öğe büyük kart olur (masaüstünde iki
 * sütun kaplar); mobilde normal kartlar sıkı satıra dönüşür (CSS).
 */
export function StoryGrid({ stories, selectedId, featured = 0 }: { stories: Story[]; selectedId?: string | null; featured?: number }) {
  const lead = stories.slice(0, featured);
  const rest = stories.slice(featured);
  return (
    <>
      {lead.length > 0 && (
        <div className="feature-row">
          {lead.map((s) => (
            <FeatureTile key={s.id} story={s} selected={s.id === selectedId} />
          ))}
        </div>
      )}
      <div className="grid">
        {rest.map((s) => (
          <StoryTile key={s.id} story={s} selected={s.id === selectedId} />
        ))}
      </div>
    </>
  );
}
